/**
 * Geometry for "export by selected area".
 *
 * Everything here works off the object's own `geoJson` / `latitude` / `longitude`
 * rather than its rendered `mapObj`. `mapObj` is not dependable: markers never
 * carry one at all, and only some circles do, which is why the old count
 * silently dropped every marker (WEB-7075).
 *
 * Bounds are plain `{ north, south, east, west }` literals so this file has no
 * dependency on the Maps API and can be reasoned about on its own.
 *
 * GeoJSON coordinates are [lng, lat]. Tracks and lines are a flat coordinate
 * list, polygons nest one ring deep, circles are a single point plus a radius
 * in metres.
 *
 * A feature that crosses the boundary counts, and is exported whole (WEB-7077).
 * Anti-meridian crossings are not handled; BaseMap's data does not span it.
 */

const EARTH_RADIUS_M = 6371008.8

function parse(raw) {
  if (!raw) return null
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw
  } catch (e) {
    return null
  }
}

/**
 * Pull drawable geometry out of a measurement.
 * Returns { kind: 'path', pts, closed } | { kind: 'circle', center, radius } | null
 */
export function geometryOf(measure, type) {
  const geo = parse(measure && measure.geoJson)
  const feature = geo && geo.features && geo.features[0]
  if (!feature || !feature.geometry) return null

  const coords = feature.geometry.coordinates
  if (!coords) return null

  if (type === 'circle') {
    const radius = feature.properties && feature.properties.radius
    if (!Number.isFinite(radius) || !Number.isFinite(coords[0])) return null
    return { kind: 'circle', center: [coords[0], coords[1]], radius }
  }

  // Polygons nest one ring deep; tracks and lines do not.
  const pts = type === 'polygon' ? coords[0] : coords
  if (!Array.isArray(pts) || pts.length < 2) return null
  return { kind: 'path', pts, closed: type === 'polygon' }
}

export function pointInBounds(lng, lat, b) {
  return lat >= b.south && lat <= b.north && lng >= b.west && lng <= b.east
}

export function markerInBounds(marker, b) {
  if (!marker || !Number.isFinite(marker.latitude) || !Number.isFinite(marker.longitude)) {
    return false
  }
  return pointInBounds(marker.longitude, marker.latitude, b)
}

/** Liang–Barsky: does the segment a→b touch the rectangle at all? */
export function segmentTouchesBounds(a, b, box) {
  const dx = b[0] - a[0]
  const dy = b[1] - a[1]
  const p = [-dx, dx, -dy, dy]
  const q = [a[0] - box.west, box.east - a[0], a[1] - box.south, box.north - a[1]]
  let t0 = 0
  let t1 = 1

  for (let i = 0; i < 4; i++) {
    if (p[i] === 0) {
      if (q[i] < 0) return false // parallel and outside this edge
    } else {
      const t = q[i] / p[i]
      if (p[i] < 0) {
        if (t > t1) return false
        if (t > t0) t0 = t
      } else {
        if (t < t0) return false
        if (t < t1) t1 = t
      }
    }
  }
  return true
}

/** Ray casting, on [lng, lat] pairs. */
function ringContainsPoint(pts, lng, lat) {
  let inside = false
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = pts[i][0]
    const yi = pts[i][1]
    const xj = pts[j][0]
    const yj = pts[j][1]
    const straddles = (yi > lat) !== (yj > lat)
    if (straddles && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
      inside = !inside
    }
  }
  return inside
}

export function pathTouchesBounds(pts, box, closed) {
  for (let i = 0; i < pts.length; i++) {
    if (pointInBounds(pts[i][0], pts[i][1], box)) return true
  }
  for (let i = 0; i < pts.length - 1; i++) {
    if (segmentTouchesBounds(pts[i], pts[i + 1], box)) return true
  }
  if (closed && pts.length > 2) {
    if (segmentTouchesBounds(pts[pts.length - 1], pts[0], box)) return true
    // A polygon large enough to swallow the whole selection has no vertex
    // inside it and no edge crossing it, so test a corner for containment.
    if (ringContainsPoint(pts, box.west, box.north)) return true
  }
  return false
}

function haversineMetres(lng1, lat1, lng2, lat2) {
  const toRad = Math.PI / 180
  const dLat = (lat2 - lat1) * toRad
  const dLng = (lng2 - lng1) * toRad
  const s1 = Math.sin(dLat / 2)
  const s2 = Math.sin(dLng / 2)
  const a = s1 * s1 + Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) * s2 * s2
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)))
}

/**
 * Nearest point on the box to the centre, then a real distance in metres —
 * avoids converting a metre radius into degrees, which is latitude dependent.
 */
export function circleTouchesBounds(center, radius, box) {
  const lng = Math.max(box.west, Math.min(center[0], box.east))
  const lat = Math.max(box.south, Math.min(center[1], box.north))
  return haversineMetres(center[0], center[1], lng, lat) <= radius
}

export function measureTouchesBounds(measure, type, box) {
  const geo = geometryOf(measure, type)
  if (!geo) return false
  return geo.kind === 'circle'
    ? circleTouchesBounds(geo.center, geo.radius, box)
    : pathTouchesBounds(geo.pts, box, geo.closed)
}

/**
 * Everything inside `box`, split the way the panel presents it.
 * `props` carries the redux arrays; `types` toggles each group.
 */
export function selectWithin(box, props, types) {
  const {
    markers = [], photos = [], tracks = [], lines = [], polygons = [], circles = []
  } = props
  const wantMarkers = !types || types.markers
  const wantPhotos = !types || types.photos
  const wantTracks = !types || types.tracks
  const wantShapes = !types || types.shapes

  const inMarkers = wantMarkers && box
    ? markers.filter(m => markerInBounds(m, box))
    : []

  // Photos carry latitude/longitude at the top level exactly as markers do.
  const inPhotos = wantPhotos && box
    ? photos.filter(p => markerInBounds(p, box))
    : []

  const inTracks = wantTracks && box
    ? tracks.filter(t => measureTouchesBounds(t, 'track', box))
    : []

  const inShapes = wantShapes && box
    ? [].concat(
      lines.filter(l => measureTouchesBounds(l, 'line', box)),
      polygons.filter(p => measureTouchesBounds(p, 'polygon', box)),
      circles.filter(c => measureTouchesBounds(c, 'circle', box))
    )
    : []

  return {
    markers: inMarkers,
    photos: inPhotos,
    tracks: inTracks,
    shapes: inShapes,
    // measurements is what the KML endpoint wants alongside marker ids
    measurements: inTracks.concat(inShapes),
    total: inMarkers.length + inPhotos.length + inTracks.length + inShapes.length
  }
}

/** Counts ignoring the type toggles, so the panel can show each row's number. */
export function countsWithin(box, props) {
  if (!box) return { markers: 0, photos: 0, tracks: 0, shapes: 0 }
  const all = selectWithin(box, props, null)
  return {
    markers: all.markers.length,
    photos: all.photos.length,
    tracks: all.tracks.length,
    shapes: all.shapes.length
  }
}
